import { extractApiErrorMessage } from "@boundless/bl-api-client/api-errors"
import {
  useCustomerAppDocRetrieveSuspense,
  useCustomerAppDocSendUpdate,
  useCustomerAppDocSignUpdate,
  useCustomerApplicationsRetrieveSuspense,
} from "@boundless/bl-api-client/customer/services"
import { usePublicBoundlessConfigRetrieveSuspense } from "@boundless/bl-api-client/public/services"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { toast } from "sonner"
import { useStore } from "zustand/react"

import { invalidateApplicationDocumentQueries } from "@/components/applications/sign/invalidateApplicationDocumentQueries"
import { SigningPage } from "@/components/applications/sign/SigningPage"
import { projectSigningPageState } from "@/components/applications/sign/signingPageState"
import { useReconcilePendingApplicationDocument } from "@/components/applications/sign/useReconcilePendingApplicationDocument"
import { ApplicationFlowContext } from "@/lib/flows/application"
import { store } from "@/lib/store"

export const Route = createFileRoute(
  "/_dashboard/_application-flow/applications/$applicationUuid/sign",
)({
  component: RouteComponent,
  beforeLoad: ({ context: { store } }) => {
    const companyUuid = store.getState().activeCompanyUuid
    if (!companyUuid) {
      throw redirect({ to: "/" })
    }
    return { companyUuid }
  },
})

function RouteComponent() {
  const activeCompanyUuid = useStore(store, (state) => state.activeCompanyUuid)
  if (!activeCompanyUuid) {
    throw redirect({ to: "/" })
  }
  const queryClient = useQueryClient()
  const flowActorRef = ApplicationFlowContext.useActorRef()

  const { applicationUuid } = Route.useParams()
  const { data: boundlessConfig } = usePublicBoundlessConfigRetrieveSuspense()

  const {
    data: {
      fundingNeeds: { uuid: fundingNeedsUuid, isSigned },
    },
  } = useCustomerApplicationsRetrieveSuspense(
    activeCompanyUuid,
    applicationUuid,
  )

  const { data: applicationDocument } = useCustomerAppDocRetrieveSuspense(
    activeCompanyUuid,
    fundingNeedsUuid,
  )

  useReconcilePendingApplicationDocument({
    companyUuid: activeCompanyUuid,
    applicationUuid,
    fundingNeedsUuid,
    isSigned,
    lifecycle: applicationDocument.lifecycle,
    signerEvidenceInvalid: applicationDocument.signerEvidenceInvalid,
  })

  const { mutate: sendForSignatures, isPending: isSending } =
    useCustomerAppDocSendUpdate({
      mutation: {
        onError: (error) => {
          toast.error(
            extractApiErrorMessage(
              error,
              "The signing request could not be sent. Please try again.",
            ),
          )
        },
        onSettled: async () => {
          await invalidateApplicationDocumentQueries(queryClient, {
            companyUuid: activeCompanyUuid,
            fundingNeedsUuid,
            applicationUuid,
          })
        },
      },
    })

  const { mutate: finalizeDocument } = useCustomerAppDocSignUpdate({
    mutation: {
      onSuccess: async () => {
        await invalidateApplicationDocumentQueries(queryClient, {
          companyUuid: activeCompanyUuid,
          fundingNeedsUuid,
          applicationUuid,
        })
      },
    },
  })

  const pageState = projectSigningPageState(applicationDocument, isSending)
  const reconcileDocument = () => {
    finalizeDocument({
      companyUuid: activeCompanyUuid,
      fundingNeedsUuid,
    })
  }

  return (
    <SigningPage
      state={pageState}
      supportEmail={boundlessConfig.supportEmail}
      onBack={() => flowActorRef.send({ type: "back" })}
      onSend={() =>
        sendForSignatures({
          companyUuid: activeCompanyUuid,
          fundingNeedsUuid,
        })
      }
      onReconcile={reconcileDocument}
      onContinue={() => flowActorRef.send({ type: "next" })}
    />
  )
}
